import useLocale from 'next-translate/useTranslation';
import { useEffect, useState } from 'react';

import clsxm from '@/lib/clsxm';

interface SelectOptions {
  options: Array<string | { value: string; label: string }>;
  label: string;
  value?: string;
  className?: string;
  disabledOptions?: string[];
  disabled?: boolean;
  onChange: (value: string) => void;
  placeholder?: string;
}

export default function Select({
  options,
  label,
  value = '',
  className = '',
  disabled = false,
  disabledOptions = [],
  onChange,
  placeholder,
}: SelectOptions) {
  const { t } = useLocale('common');
  const [internalValue, setInternalValue] = useState(value);

  useEffect(() => {
    setInternalValue((current) => (current === value ? current : value));
  }, [value]);

  const getOptionKey = (option: string | { value: string; label: string }) =>
    typeof option === 'string' ? option : option.value;

  const getOptionValue = (option: string | { value: string; label: string }) =>
    typeof option === 'string' ? option : option.value;

  const getOptionLabel = (option: string | { value: string; label: string }) =>
    typeof option === 'string' ? t(option) : t(option.label);

  const isOptionDisabled = (
    option: string | { value: string; label: string },
  ) => disabledOptions.includes(getOptionValue(option));

  return (
    <select
      id={label}
      name={label}
      disabled={disabled}
      className={clsxm(
        'mt-1 block w-full rounded-md border-gray-300 py-2 pl-3 pr-10 text-base focus:border-primary-500 focus:outline-none focus:ring-primary-500 dark:border-white/5 dark:bg-slate-700/50 dark:text-white sm:text-sm',
        className,
      )}
      onChange={(e) => {
        onChange(e.target.value);
        setInternalValue(e.target.value);
      }}
      value={internalValue}
    >
      <option value='' disabled hidden>
        {placeholder || t('Select an option')}
      </option>
      {options.map((option) => (
        <option
          key={getOptionKey(option)}
          value={getOptionValue(option)}
          disabled={isOptionDisabled(option)}
        >
          {getOptionLabel(option)}
        </option>
      ))}
    </select>
  );
}
